import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
    BookOpen, Calendar, Clock, ArrowRight, 
    Sparkles, Compass, Eye, Loader2 
} from 'lucide-react';
import { blogService } from '../services/blogService';

const LatestBlogs = () => {
    const [blogs, setBlogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    const apiBase = (process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8001/api/v1').replace(/\/api\/v1\/?$/, '');

    useEffect(() => {
        const fetchLatestBlogs = async () => {
            try {
                const res = await blogService.getBlogs();
                const list = res?.data?.data || res?.data || [];
                // Pick the top/latest 3 blogs
                setBlogs(Array.isArray(list) ? list.slice(0, 3) : []);
            } catch (err) {
                console.error('Error fetching latest blogs on home page:', err);
            } finally {
                setLoading(false);
            }
        };

        fetchLatestBlogs();
    }, []);

    // Helper to format date
    const formatDate = (dateStr) => {
        if (!dateStr) return '';
        try {
            return new Date(dateStr).toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric'
            });
        } catch {
            return dateStr;
        }
    };

    // Calculate approximate read time
    const getReadTime = (subcontent = '') => {
        const words = subcontent.trim().split(/\s+/).length;
        const minutes = Math.max(3, Math.ceil(words / 40));
        return `${minutes} min read`;
    };

    // Placeholder fallback image if blog has no cover image
    const fallbackImage = "data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='800' height='450' viewBox='0 0 800 450'><rect width='100%' height='100%' fill='%23f1f5f9'/><text x='50%' y='50%' dominant-baseline='middle' text-anchor='middle' font-family='sans-serif' font-size='24' font-weight='bold' fill='%2394a3b8'>ZoyoTrip Travel Guide</text></svg>";

    if (loading) {
        return (
            <section className="py-4 py-md-5 bg-white">
                <div className="container">
                    <div className="d-flex justify-content-center py-5">
                        <Loader2 className="animate-spin text-primary" size={36} />
                    </div>
                </div>
            </section>
        );
    }

    if (!blogs || blogs.length === 0) {
        return null; // Don't show empty block if no blogs exist
    }

    return (
        <section className="py-4 py-md-5 bg-white position-relative">
            <div className="container">

                {/* Section Header */}
                <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-end gap-3 mb-4 mb-md-5">
                    <div>
                        <div className="d-inline-flex align-items-center gap-1.5 px-3 py-1 rounded-pill bg-primary bg-opacity-10 text-primary small fw-bold mb-2">
                            <BookOpen size={14} className="text-primary" />
                            <span>TRAVEL INSPIRATION & STORIES</span>
                        </div>
                        <h2 className="fw-black text-dark mb-1 fs-2" style={{ letterSpacing: '-0.5px' }}>
                            Latest Travel Guides & Stories
                        </h2>
                        <p className="text-muted mb-0 small">
                            Curated itineraries, sacred temple circuits, and insider recommendations for your next trip
                        </p>
                    </div>

                    <Link 
                        to="/blogs"
                        className="btn btn-outline-primary rounded-pill px-4 py-2 fw-semibold d-inline-flex align-items-center gap-2 transition hover-lift"
                        style={{ fontSize: '13.5px' }}
                    >
                        <span>Explore All Blogs</span>
                        <ArrowRight size={15} />
                    </Link>
                </div>

                {/* 3 Latest Blog Cards */}
                <div className="row g-3 g-lg-4">
                    {blogs.map((blog, index) => {
                        const coverUrl = blog.cover_img 
                            ? `${apiBase}/storage/${blog.cover_img}` 
                            : fallbackImage;

                        return (
                            <div key={blog.id || index} className="col-12 col-md-4">
                                <article 
                                    className="card h-100 border rounded-4 overflow-hidden shadow-xs hover-card-premium transition cursor-pointer bg-white"
                                    onClick={() => navigate(`/blogs/${blog.slug}`)}
                                    style={{ cursor: 'pointer' }}
                                >
                                    {/* Cover Image Container */}
                                    <div className="position-relative overflow-hidden" style={{ height: '210px', background: '#f8fafc' }}>
                                        <img 
                                            src={coverUrl} 
                                            alt={blog.title} 
                                            className="w-100 h-100 object-fit-cover blog-img-zoom transition"
                                            loading="lazy"
                                            onError={(e) => {
                                                e.target.onerror = null;
                                                e.target.src = fallbackImage;
                                            }}
                                        />

                                        {/* Floating Category Pill */}
                                        <div className="position-absolute top-0 start-0 m-3" style={{ zIndex: 2 }}>
                                            <span 
                                                className="badge rounded-pill bg-white bg-opacity-95 text-dark fw-bold px-2.5 py-1.5 shadow-sm d-inline-flex align-items-center gap-1.5"
                                                style={{ fontSize: '11px', backdropFilter: 'blur(6px)' }}
                                            >
                                                <Compass size={12} className="text-primary" />
                                                <span>Travel Guide</span>
                                            </span>
                                        </div>

                                        {/* Floating Reading Time */}
                                        <div className="position-absolute bottom-0 end-0 m-3" style={{ zIndex: 2 }}>
                                            <span 
                                                className="badge rounded-pill bg-dark bg-opacity-75 text-white fw-semibold px-2 py-1 shadow-sm d-inline-flex align-items-center gap-1"
                                                style={{ fontSize: '10.5px', backdropFilter: 'blur(4px)' }}
                                            >
                                                <Clock size={11} />
                                                <span>{getReadTime(blog.subcontent)}</span>
                                            </span>
                                        </div>
                                    </div>

                                    {/* Card Body */}
                                    <div className="card-body p-3.5 p-lg-4 d-flex flex-column">
                                        
                                        {/* Meta date */}
                                        <div className="d-flex align-items-center gap-2 text-muted small mb-2" style={{ fontSize: '12px' }}>
                                            <Calendar size={13} className="text-primary flex-shrink-0" />
                                            <span>{formatDate(blog.created_at)}</span>
                                            {blog.views !== undefined && blog.views !== null && (
                                                <>
                                                    <span>•</span>
                                                    <span className="d-flex align-items-center gap-1">
                                                        <Eye size={12} />
                                                        <span>{blog.views} views</span>
                                                    </span>
                                                </>
                                            )}
                                        </div>

                                        {/* Title */}
                                        <h5 
                                            className="fw-bold text-dark mb-2 fs-6 lh-base blog-title transition"
                                            style={{
                                                display: '-webkit-box',
                                                WebkitLineClamp: 2,
                                                WebkitBoxOrient: 'vertical',
                                                overflow: 'hidden',
                                                minHeight: '44px'
                                            }}
                                            title={blog.title}
                                        >
                                            {blog.title}
                                        </h5>

                                        {/* Excerpt */}
                                        <p 
                                            className="text-muted small mb-3 flex-grow-1"
                                            style={{
                                                display: '-webkit-box',
                                                WebkitLineClamp: 3,
                                                WebkitBoxOrient: 'vertical',
                                                overflow: 'hidden',
                                                fontSize: '13px',
                                                lineHeight: '1.55'
                                            }}
                                        >
                                            {blog.subcontent}
                                        </p>

                                        {/* Read More Link */}
                                        <div className="pt-3 border-top border-light-subtle d-flex align-items-center justify-content-between mt-auto">
                                            <span className="fw-bold text-primary small d-inline-flex align-items-center gap-1.5 read-more-link">
                                                <span>Read Article</span>
                                                <ArrowRight size={14} className="transition-arrow" />
                                            </span>
                                            <span className="badge bg-light text-secondary rounded-pill px-2.5 py-1" style={{ fontSize: '11px' }}>
                                                ZoyoTrip Editorial
                                            </span>
                                        </div>

                                    </div>
                                </article>
                            </div>
                        );
                    })}
                </div>

                {/* Mobile View All CTA Button */}
                <div className="d-block d-md-none text-center mt-4">
                    <Link 
                        to="/blogs"
                        className="btn btn-primary rounded-pill w-100 py-2.5 fw-bold d-inline-flex align-items-center justify-content-center gap-2 shadow-sm"
                    >
                        <span>Explore All Travel Stories</span>
                        <ArrowRight size={16} />
                    </Link>
                </div>

            </div>

            <style>{`
                .hover-card-premium {
                    transition: transform 0.25s ease, box-shadow 0.25s ease, border-color 0.25s ease;
                }
                .hover-card-premium:hover {
                    transform: translateY(-4px);
                    box-shadow: 0 12px 28px rgba(15, 23, 42, 0.08) !important;
                    border-color: #cbd5e1 !important;
                }
                .hover-card-premium:hover .blog-img-zoom {
                    transform: scale(1.05);
                }
                .hover-card-premium:hover .blog-title {
                    color: #0d6efd !important;
                }
                .hover-card-premium:hover .transition-arrow {
                    transform: translateX(4px);
                }
                .blog-img-zoom {
                    transition: transform 0.4s cubic-bezier(0.4, 0, 0.2, 1);
                }
                .transition-arrow {
                    transition: transform 0.2s ease;
                }
                .hover-lift {
                    transition: transform 0.2s ease, box-shadow 0.2s ease;
                }
                .hover-lift:hover {
                    transform: translateY(-2px);
                }
                .shadow-xs {
                    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04) !important;
                }
                .animate-spin {
                    animation: spin 1s linear infinite;
                }
                @keyframes spin {
                    from { transform: rotate(0deg); }
                    to { transform: rotate(360deg); }
                }
            `}</style>
        </section>
    );
};

export default LatestBlogs;
